import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { CalendarCheck, RefreshCw, StopCircle, Activity, ListChecks, Settings2 } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Notice, Pill, Stats, Stat, Item, Empty, Loading, Pager, Progress } from '../components/gtpl/GtplKit';

const TYPE_PREF_KEY = 'gtplExpiryCheck.types';
const TYPES = [
  { key: 'regular', label: 'Regular' },
  { key: 'dtd', label: 'DTD' },
  { key: 'amp', label: 'AMP' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.key, t.label]));

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}
function fmtDay(d) {
  return d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

// CRM expiry date vs the live GTPL Saathi end date, for the customer types ticked (DTD by
// default). Check-only — nothing here writes to GTPL or the CRM. Each customer costs one real
// GTPL lookup, so a run happens in the background and this page polls for progress.
export default function GtplExpiryCheckPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canCheck = hasModuleRight('gtplSaathi', 'expiryCheck');

  const [run, setRun] = useState(null);
  const [types, setTypes] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(TYPE_PREF_KEY) || 'null');
      const valid = Array.isArray(saved) ? saved.filter((t) => TYPE_LABEL[t]) : [];
      return valid.length ? valid : ['dtd'];
    } catch { return ['dtd']; }
  });
  const [count, setCount] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [onlyMismatches, setOnlyMismatches] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [loadingRows, setLoadingRows] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const pollRef = useRef(null);

  const loadRows = useCallback(async (runId) => {
    if (!runId) { setRows([]); return; }
    setLoadingRows(true);
    try {
      const { data } = await gtplSaathiAPI.getExpiryCheckRows({ runId, onlyMismatches, durationType: typeFilter || undefined, page, limit: 50 });
      setRows(data.rows || []);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load rows');
    } finally {
      setLoadingRows(false);
    }
  }, [onlyMismatches, typeFilter, page]);

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.getExpiryCheckLatest();
      setRun(data.data || null);
      return data.data || null;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load check status');
      return null;
    }
  }, []);

  useEffect(() => { loadLatest().then((r) => loadRows(r?._id)); }, []); // eslint-disable-line
  useEffect(() => { if (run?._id) loadRows(run._id); }, [onlyMismatches, typeFilter, page]); // eslint-disable-line

  useEffect(() => {
    try { localStorage.setItem(TYPE_PREF_KEY, JSON.stringify(types)); } catch { /* private mode */ }
    if (!types.length) { setCount(null); return; }
    gtplSaathiAPI.getExpiryCheckCount(types).then(({ data }) => setCount(data.data)).catch(() => setCount(null));
  }, [types]);

  useEffect(() => {
    if (run?.status !== 'running') { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const latest = await loadLatest();
      loadRows(latest?._id);
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [run?.status, loadLatest, loadRows]);

  const toggleType = (key) => setTypes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const handleRun = async () => {
    if (!types.length) { toast.error('Pick at least one customer type'); return; }
    setStarting(true);
    try {
      const { data } = await gtplSaathiAPI.runExpiryCheck(types);
      setRun(data.data);
      setPage(1);
      setRows([]);
      toast.success('Expiry check started — this runs in the background');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start check');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      const { data } = await gtplSaathiAPI.stopExpiryCheck();
      setRun(data.data);
      toast.success('Stop requested — finishing the customer in progress, then it will halt');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to stop');
    } finally {
      setStopping(false);
    }
  };

  const isRunning = run?.status === 'running';
  const mismatchCount = (run?.mismatches || 0) + (run?.errorCount || 0);
  const runTone = isRunning ? 'info' : run?.status === 'failed' ? 'bad' : run?.status === 'cancelled' ? 'mute' : 'ok';
  const runLabel = isRunning ? (run.cancelRequested ? 'Stopping…' : 'Running') : run?.status === 'failed' ? 'Failed' : run?.status === 'cancelled' ? 'Stopped' : 'Completed';

  return (
    <Screen>
      <PageHead icon={CalendarCheck} tone="ok" title="Expiry Check"
        sub="Compares each active CableTV customer's expiry date in the CRM with the end date of their latest package on gtplsaathi.com. Check-only — nothing is changed on either side. Each customer is one real GTPL lookup, so a run takes a while and keeps going in the background if you leave this page.">
        {isRunning && (
          <button type="button" className="btn btn-sm btn-danger" disabled={stopping || run?.cancelRequested || !canCheck} onClick={handleStop}>
            <StopCircle size={14} /> {run?.cancelRequested || stopping ? 'Stopping…' : 'Stop'}
          </button>
        )}
        <button type="button" className="btn btn-sm btn-primary" disabled={starting || isRunning || !canCheck || !types.length} onClick={handleRun}>
          <RefreshCw size={14} style={starting || isRunning ? { animation: 'spin 1s linear infinite' } : undefined} />
          {isRunning ? 'Checking…' : starting ? 'Starting…' : 'Run Expiry Check'}
        </button>
      </PageHead>

      {!canCheck && <Notice tone="bad">You don't have permission to run the expiry check.</Notice>}

      <Panel icon={Settings2} tone="info" title="Customer types to check"
        sub={!types.length ? 'Pick at least one type.'
          : count ? `This run would check ${count.selected} customer${count.selected === 1 ? '' : 's'} — about ${Math.max(1, Math.round(count.selected * 1.2 / 60))} min or more.` : undefined}>
        <div className="gx-actions">
          {TYPES.map((t) => {
            const on = types.includes(t.key);
            return (
              <button key={t.key} type="button" aria-pressed={on} disabled={isRunning}
                className={`btn btn-sm ${on ? 'btn-primary' : 'btn-secondary'}`} onClick={() => toggleType(t.key)}>
                {t.label}{count?.counts ? ` (${count.counts[t.key]})` : ''}
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel icon={Activity} tone={run ? runTone : 'mute'} title={run ? `Latest check: ${runLabel}` : 'Latest check'}
        sub={run ? `${(run.durationTypes || []).map((t) => TYPE_LABEL[t]).join(' + ') || '—'} · ${isRunning ? 'started' : 'completed'} ${fmtDateTime(isRunning ? run.startedAt : run.completedAt)}` : undefined}>
        {!run ? (
          <Empty icon={CalendarCheck} title="No expiry check has been run yet" />
        ) : (
          <>
            <Stats>
              <Stat tone="info" value={`${run.checked || 0} / ${run.totalCustomers || 0}`} label="Checked" />
              <Stat tone="ok" value={run.matches || 0} label="Matches" />
              <Stat tone={mismatchCount ? 'bad' : 'mute'} value={run.mismatches || 0} label="Different" />
              <Stat tone={run.errorCount ? 'warn' : 'mute'} value={run.errorCount || 0} label="Errors" />
            </Stats>
            {isRunning && <Progress value={run.checked || 0} max={run.totalCustomers || 0} tone="info" />}
            {run.failMessage && (
              <div style={{ marginTop: 12 }}>
                <Notice tone={run.status === 'cancelled' ? 'mute' : 'bad'}
                  actions={/session expired/i.test(run.failMessage) ? <Link to="/gtpl-saathi-settings" style={{ fontWeight: 600 }}>Paste a fresh cookie →</Link> : null}>
                  {run.failMessage}
                </Notice>
              </div>
            )}
          </>
        )}
      </Panel>

      {run && (
        <Panel icon={ListChecks} tone="mute" title="Results" count={pagination.total}
          right={
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loadingRows} onClick={() => loadRows(run._id)}>
              <RefreshCw size={13} style={loadingRows ? { animation: 'spin 1s linear infinite' } : undefined} /> Refresh
            </button>}>
          <div className="gx-actions" style={{ marginBottom: 12 }}>
            <button type="button" aria-pressed={onlyMismatches} className={`btn btn-sm ${onlyMismatches ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setOnlyMismatches(!onlyMismatches); setPage(1); }}>
              Only differences / errors
            </button>
            <select className="form-control" style={{ width: 'auto' }} value={typeFilter} aria-label="Type"
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All types</option>
              {(run.durationTypes?.length ? run.durationTypes : TYPES.map((t) => t.key)).map((t) => (
                <option key={t} value={t}>{TYPE_LABEL[t]}</option>
              ))}
            </select>
          </div>

          {loadingRows ? (
            <Loading />
          ) : rows.length === 0 ? (
            <Empty tone={onlyMismatches ? 'ok' : undefined} title={onlyMismatches ? 'No differences or errors found' : (isRunning ? 'Results will appear here as the check progresses…' : 'Nothing to show')} />
          ) : (
            <div className="gx-list">
              {rows.map((r) => {
                const differs = r.status === 'mismatch';
                const tone = differs ? 'bad' : r.status === 'error' ? 'warn' : r.status === 'match' ? 'ok' : undefined;
                return (
                  <Item key={r._id} tone={tone}
                    title={<Link to={`/customers/${r.customer}`}>{r.name}</Link>}
                    sub={<>Bill No. {r.billingNumber || '—'}{r.area ? ` · ${r.area}` : ''} · <span style={{ fontFamily: 'monospace' }}>{r.accountNumber}</span></>}
                    right={differs ? (
                      <Pill tone="bad" dot>{Math.abs(r.diffDays)} day{Math.abs(r.diffDays) === 1 ? '' : 's'} {r.diffDays > 0 ? 'later on GTPL' : 'earlier on GTPL'}</Pill>
                    ) : r.status === 'match' ? <Pill tone="ok" dot>Same</Pill>
                      : r.status === 'error' ? <Pill tone="warn" dot>Error</Pill> : null}
                    meta={<>
                      <span>{TYPE_LABEL[r.durationType] || r.durationType}</span>
                      <span>CRM expiry: {fmtDay(r.crmExpiry)}</span>
                      <span>GTPL expiry: {fmtDay(r.gtplExpiry)}</span>
                      {r.status === 'error' && <span>{r.error}</span>}
                    </>} />
                );
              })}
            </div>
          )}

          <Pager page={page} pages={pagination.pages} total={pagination.total} onPage={setPage} />
        </Panel>
      )}
    </Screen>
  );
}
